/**
 * TerminationsTab — Gestión de finiquitos con modal de detalle completo.
 * Al hacer clic en un registro se abre un modal con todas las variables de cálculo.
 */
import { useState, useRef } from 'react'
import { Plus, Calculator, CheckCircle, Trash2, Pencil, Info, FileText, Printer } from 'lucide-react'
import { useTerminations, useCreateTermination, useCalculateTermination, useConfirmTermination, useDeleteTermination, useUpdateTermination, useEmployees, type Termination } from '../hooks/usePayroll'
import { usePermission } from '@/hooks/usePermission'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { ToastContainer, useToast } from '@/components/Toast'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { Modal } from '@/components/ds/Modal'
import { extractErrorMessage } from '@/lib/errorUtils'
import DatePicker from '@/components/DatePicker'
import { labelCls } from '@/lib/formStyles'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { SETTLEMENT_STATUS } from '../../../components/ds/statusCatalogs'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'


const REASON_LABELS: Record<string, string> = {
  renuncia: 'Renuncia Voluntaria',
  mutuo_acuerdo: 'Mutuo Acuerdo',
  necesidades_empresa: 'Necesidades de la Empresa (Art. 161)',
  art_160: 'Causal Disciplinaria (Art. 160)',
  vencimiento_plazo: 'Vencimiento del Plazo',
  fin_obra: 'Conclusión de la Obra',
  caso_fortuito: 'Caso Fortuito',
}

const CONTRACT_TYPES: Record<string, string> = {
  indefinido: 'Indefinido',
  plazo_fijo: 'Plazo Fijo',
  por_obra: 'Por Obra o Faena',
  honorarios: 'Honorarios',
}

export default function TerminationsTab() {
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()

  const { data, isLoading } = useTerminations()
  const terminations: Termination[] = data?.terminations || []
  const selected = terminations.find(t => t.id === selectedId) || null

  const fmt = (v: string | number) => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return `$${formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)}`
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{terminations.length} finiquitos</span>
        {can('payroll.terminations', 'create') && (
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nuevo finiquito
          </button>
        )}
      </div>

      {creating && <TerminationForm onClose={() => setCreating(false)} toast={toast} />}

      <DataTable<Termination>
        columns={[
          { key: 'employee_name', header: 'Empleado', sortable: true,
            render: t => <span className="font-medium text-content">{t.employee_name}</span> },
          { key: 'termination_date', header: 'Fecha', sortable: true,
            render: t => <span className="text-content-muted">{formatDate(t.termination_date, locale)}</span> },
          { key: 'reason', header: 'Causal', sortable: true,
            sortValue: t => t.reason_display || REASON_LABELS[t.reason] || t.reason,
            render: t => (
              <span className="text-content-muted">
                {t.reason_display || REASON_LABELS[t.reason] || t.reason}
              </span>
            ) },
          { key: 'total_settlement', header: 'Total Finiquito', numeric: true, sortable: true,
            sortValue: t => Number(t.total_settlement),
            render: t => <span className="font-bold text-primary">{fmt(t.total_settlement)}</span> },
          { key: 'status', header: 'Estado', sortable: true,
            render: t => <StatusBadge status={t.status} catalog={SETTLEMENT_STATUS} /> },
        ]}
        rows={terminations}
        rowKey={t => t.id}
        minWidth={760}
        loading={isLoading}
        onRowClick={t => setSelectedId(t.id)}
        empty={<EmptyState title="Sin finiquitos registrados." size="sm" />}
        caption="Finiquitos calculados y su estado"
      />

      {/* Modal de detalle */}
      {selected && (
        <TerminationModal
          termination={selected}
          onClose={() => setSelectedId(null)}
          fmt={fmt}
          toast={toast}
          can={can}
        />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Modal completo del finiquito
   ══════════════════════════════════════════════════════════════════════════════ */

function TerminationModal({ termination: t, onClose, fmt, toast, can }: {
  termination: Termination; onClose: () => void
  fmt: (v: string | number) => string; toast: any; can: any
}) {
  const [editing, setEditing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const hasCalc = !!(t.calculation_details?.hire_date)

  const handlePrint = () => {
    if (!contentRef.current) return
    const pw = window.open('', '_blank', 'width=900,height=1000')
    if (!pw) return
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML).join('\n')
    pw.document.write(`
      <!DOCTYPE html><html><head><title>Finiquito — ${t.employee_name}</title>
      ${styles}
      <style>
        @page { margin: 14mm; }
        body { background: white !important; padding: 0 !important; }
        .print-sigs { display: flex; justify-content: space-between; margin-top: 56px; }
        .print-sig { width: 40%; text-align: center; }
        .print-sig .line { border-top: 1px solid #94a3b8; margin-bottom: 4px; }
        .print-sig .label { font-size: 11px; color: #64748b; }
        .print-footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0;
          font-size: 10px; color: #94a3b8; text-align: center; }
      </style></head><body>
      <div style="max-width: 780px; margin: 0 auto; padding: 16px;">
        ${contentRef.current.innerHTML}
        <div class="print-sigs">
          <div class="print-sig"><div class="line">&nbsp;</div><div class="label">Firma Empleador</div></div>
          <div class="print-sig"><div class="line">&nbsp;</div><div class="label">Firma Trabajador</div></div>
        </div>
        <div class="print-footer">
          Documento generado por Clobi ERP — ${new Date().toLocaleDateString('es-CL')}
        </div>
      </div>
      <script>setTimeout(function(){window.print()},500)</script>
      </body></html>
    `)
    pw.document.close()
  }

  return (
    <Modal open onClose={onClose} size="2xl"
      title={`Finiquito — ${t.employee_name}`}
      actions={
        <TerminationModalFooter termination={t} toast={toast} can={can} onClose={onClose} onPrint={handlePrint} canPrint={hasCalc} />
      }
    >
      <div className="space-y-5">
        {/* Badge de estado */}
        <div className="flex items-center justify-between">
          <StatusBadge status={t.status} catalog={SETTLEMENT_STATUS} />
          {['draft', 'calculated'].includes(t.status) && !editing && (
            <button onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <Pencil size={10} /> Editar datos
            </button>
          )}
        </div>

        {editing ? (
          <TerminationEditForm termination={t} onClose={() => setEditing(false)} fmt={fmt} />
        ) : (
          <div ref={contentRef}>
            <TerminationDetailContent termination={t} fmt={fmt} />
          </div>
        )}
      </div>
    </Modal>
  )
}

/* ── Footer del modal con acciones ─────────────────────────────────────────── */

function TerminationModalFooter({ termination: t, toast, can, onClose, onPrint, canPrint }: {
  termination: Termination; toast: any; can: any; onClose: () => void; onPrint: () => void; canPrint: boolean
}) {
  const { mutate: calculate, isPending: calculating } = useCalculateTermination(t.id)
  const { mutate: confirm, isPending: confirming } = useConfirmTermination(t.id)
  const { mutate: remove, isPending: removing } = useDeleteTermination(t.id)
  const { confirm: confirmDialog, modalProps } = useConfirmModal()

  const editable = ['draft', 'calculated'].includes(t.status)

  return (
    <>
      <div className="flex items-center gap-2 w-full">
        {/* Izquierda: eliminar + imprimir */}
        <div className="flex-1 flex items-center gap-2">
          {canPrint && (
            <button onClick={onPrint}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
              <Printer size={13} /> Imprimir / PDF
            </button>
          )}
          {editable && can('payroll.terminations', 'create') && (
            <button onClick={() => confirmDialog({
              message: '¿Eliminar este finiquito?',
              variant: 'danger',
              onConfirm: () => remove(undefined, {
                onSuccess: () => { toast.success('Finiquito eliminado'); onClose() },
                onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
              }),
            })} disabled={removing}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface disabled:opacity-40">
              <Trash2 size={13} /> Eliminar
            </button>
          )}
        </div>

        {/* Derecha: calcular + confirmar */}
        <div className="flex items-center gap-2">
          {editable && can('payroll.terminations', 'create') && (
            <button onClick={() => calculate(undefined, {
              onSuccess: () => toast.success(t.status === 'calculated' ? 'Finiquito recalculado' : 'Finiquito calculado'),
              onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
            })} disabled={calculating}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-40">
              <Calculator size={13} /> {t.status === 'calculated' ? 'Recalcular' : 'Calcular'}
            </button>
          )}
          {t.status === 'calculated' && can('payroll.terminations', 'approve') && (
            <button onClick={() => confirmDialog({
              message: `¿Confirmar el finiquito de ${t.employee_name}? El trabajador será marcado como desvinculado.`,
              variant: 'success',
              onConfirm: () => confirm(undefined, {
                onSuccess: () => { toast.success('Finiquito confirmado. Trabajador desvinculado.'); onClose() },
                onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
              }),
            })} disabled={confirming}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-success-fill rounded-lg hover:bg-success-fill-hover disabled:opacity-40">
              <CheckCircle size={13} /> Confirmar y Pagar
            </button>
          )}
        </div>
      </div>
      <ConfirmModal {...modalProps} />
    </>
  )
}

/* ── Contenido de detalle (dentro del modal) ───────────────────────────────── */

function TerminationDetailContent({ termination: t, fmt }: { termination: Termination; fmt: (v: string | number) => string }) {
  const locale = useTenantLocale()
  const d = t.calculation_details || {}
  const hasCalc = !!d.hire_date
  const payslips: any[] = d.last_payslips || []

  // Inferir flags para datos calculados antes de que existieran estos campos
  const qualifiesSeverance = d.qualifies_for_severance ?? parseFloat(t.severance_amount) > 0
  const qualifiesNotice = d.qualifies_for_notice ?? parseFloat(t.notice_period_payout) > 0
  const noticeApplies = d.notice_applies ?? parseFloat(t.notice_period_payout) > 0

  // Calcular antigüedad desde fechas si no existen campos desglosados
  let yrs: number | string = d.service_years
  let mos: number | string = d.service_months
  let dys: number | string = d.service_days
  if (yrs == null && d.hire_date && d.termination_date) {
    const h = new Date(d.hire_date), t2 = new Date(d.termination_date)
    let y = t2.getFullYear() - h.getFullYear()
    let m = t2.getMonth() - h.getMonth()
    let dd = t2.getDate() - h.getDate()
    if (dd < 0) { m--; dd += new Date(t2.getFullYear(), t2.getMonth(), 0).getDate() }
    if (m < 0) { y--; m += 12 }
    yrs = y; mos = m; dys = dd
  }
  yrs = yrs ?? 0; mos = mos ?? 0; dys = dys ?? 0
  const totalYrs = d.total_years_decimal ? parseFloat(d.total_years_decimal).toFixed(2) : (d.years_of_service ? parseFloat(d.years_of_service).toFixed(2) : (Number(yrs) + Number(mos) / 12).toFixed(2))
  const sevYearsCapped = d.severance_years_capped ?? t.severance_years
  const sevPerYear = d.severance_per_year ?? d.base_salary
  const dailyRate = d.daily_rate ?? '0'
  const vacDays = d.vacation_days_accrued ? parseFloat(d.vacation_days_accrued).toFixed(2) : '—'
  const daysUntil = d.days_until_termination ?? '—'

  if (!hasCalc) {
    return (
      <div className="p-6 bg-warning-surface border border-warning-border rounded-lg text-center">
        <Info size={18} className="inline mr-1.5 text-warning-solid" />
        <span className="text-sm text-warning-content">
          Finiquito en borrador. Presione <strong>Calcular</strong> para generar los montos según la legislación chilena.
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* ── Datos del trabajador y contrato ───────────────────────────── */}
      <div className="bg-glass-3 rounded-xl p-4">
        <SectionTitle>Datos del Trabajador</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
          <DataField label="Trabajador" value={t.employee_name} />
          <DataField label="Fecha Ingreso" value={formatDate(d.hire_date, locale)} />
          <DataField label="Fecha Desvinculación" value={formatDate(d.termination_date, locale)} />
          <DataField label="Antigüedad" value={`${yrs} años, ${mos} meses, ${dys} días`} />
          <DataField label="Causal" value={t.reason_display || REASON_LABELS[t.reason] || t.reason} />
          {d.contract_type && <DataField label="Tipo Contrato" value={CONTRACT_TYPES[d.contract_type] || d.contract_type} />}
          {d.contract_start_date && <DataField label="Inicio Contrato" value={formatDate(d.contract_start_date, locale)} />}
          <DataField label="Última Remuneración" value={fmt(d.base_salary)} highlight />
        </div>
      </div>

      {/* ── Variables de cálculo (3 cards) ────────────────────────────── */}
      <div>
        <SectionTitle>Desglose del Cálculo</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          {/* Indemnización por Años */}
          <CalcCard
            title="Indemnización por Años de Servicio"
            subtitle="Art. 163 CT — 1 mes por año, máx 11"
            applies={qualifiesSeverance}
            amount={t.severance_amount}
            fmt={fmt}
          >
            {qualifiesSeverance ? (
              <>
                <CalcVar label="Años para indemnización" value={`${sevYearsCapped} años (tope 11)`} />
                <CalcVar label="Valor por año" value={fmt(sevPerYear)} />
                <CalcFormula>{sevYearsCapped} × {fmt(sevPerYear)} = {fmt(t.severance_amount)}</CalcFormula>
              </>
            ) : (
              <p className="text-xs text-content-subtle italic">La causal seleccionada no da derecho a indemnización por años de servicio.</p>
            )}
          </CalcCard>

          {/* Vacaciones Proporcionales */}
          <CalcCard
            title="Vacaciones Proporcionales"
            subtitle="15 días hábiles/año, proporcional al tiempo trabajado"
            applies
            amount={t.vacation_payout}
            fmt={fmt}
          >
            <CalcVar label="Valor día" value={`${fmt(dailyRate)} (sueldo base ÷ 30)`} />
            <CalcVar label="Días proporcionales" value={`${vacDays} días`} />
            <CalcFormula>{vacDays} días × {fmt(dailyRate)} = {fmt(t.vacation_payout)}</CalcFormula>
          </CalcCard>

          {/* Aviso Previo */}
          <CalcCard
            title="Aviso Previo (30 días)"
            subtitle={qualifiesNotice
              ? (noticeApplies
                ? `${daysUntil} días hasta desvinculación (< 30)`
                : `${daysUntil} días hasta desvinculación (≥ 30)`)
              : 'Solo aplica a Art. 161 y caso fortuito'}
            applies={noticeApplies}
            amount={t.notice_period_payout}
            fmt={fmt}
          >
            {qualifiesNotice ? (
              <>
                <CalcVar label="Días hasta desvinculación" value={`${daysUntil} días`} />
                <CalcVar label="¿Menos de 30 días?" value={noticeApplies ? 'Sí → 1 mes sueldo base' : 'No → $0'} />
                {noticeApplies && (
                  <CalcFormula>1 × {fmt(d.base_salary)} = {fmt(t.notice_period_payout)}</CalcFormula>
                )}
              </>
            ) : (
              <p className="text-xs text-content-subtle italic">La causal seleccionada no requiere aviso previo.</p>
            )}
          </CalcCard>
        </div>
      </div>

      {/* ── Total ────────────────────────────────────────────────────── */}
      <div className="p-4 bg-primary-50 border border-primary-100 rounded-xl">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-content-muted">Indemnización por Años</span>
            <span className="font-semibold text-content">{fmt(t.severance_amount)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-content-muted">Vacaciones Proporcionales</span>
            <span className="font-semibold text-content">{fmt(t.vacation_payout)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-content-muted">Aviso Previo</span>
            <span className="font-semibold text-content">{fmt(t.notice_period_payout)}</span>
          </div>
          <div className="border-t border-primary-200 pt-2 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-content">TOTAL FINIQUITO</span>
              <span className="text-2xl font-black text-primary">{fmt(t.total_settlement)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Últimas liquidaciones ─────────────────────────────────────── */}
      {payslips.length > 0 && (
        <div>
          <SectionTitle icon={<FileText size={12} />}>Últimas {payslips.length} Liquidaciones (Referencia)</SectionTitle>
          <DataTable<any>
            columns={[
              { key: 'period', header: 'Período', sortable: true,
                render: ps => <span className="font-medium text-content">{fmtPeriod(ps.period)}</span> },
              { key: 'base_salary', header: 'Sueldo Base', numeric: true, sortable: true,
                sortValue: ps => Number(ps.base_salary),
                render: ps => <span className="text-content">{fmt(ps.base_salary)}</span> },
              { key: 'gratification', header: 'Gratificación', numeric: true, sortable: true,
                sortValue: ps => Number(ps.gratification),
                render: ps => <span className="text-content">{fmt(ps.gratification)}</span> },
              { key: 'total_bonuses', header: 'Bonos', numeric: true, sortable: true,
                sortValue: ps => Number(ps.total_bonuses),
                render: ps => <span className="text-content">{fmt(ps.total_bonuses)}</span> },
              { key: 'gross_salary', header: 'Bruto', numeric: true, sortable: true,
                sortValue: ps => Number(ps.gross_salary),
                render: ps => <span className="font-semibold text-content">{fmt(ps.gross_salary)}</span> },
              { key: 'total_deductions', header: 'Descuentos', numeric: true, sortable: true,
                sortValue: ps => Number(ps.total_deductions),
                render: ps => (
                  <span className="text-danger-content-soft font-medium">-{fmt(ps.total_deductions)}</span>
                ) },
              { key: 'net_salary', header: 'Líquido', numeric: true, sortable: true,
                sortValue: ps => Number(ps.net_salary),
                render: ps => <span className="font-bold text-primary">{fmt(ps.net_salary)}</span> },
            ]}
            rows={payslips}
            rowKey={(_, i) => i}
            minWidth={820}
            className="mt-2"
            caption="Liquidaciones que componen la base de cálculo del finiquito"
          />
        </div>
      )}

      {/* Notas */}
      {t.notes && (
        <div className="text-xs">
          <span className="font-semibold text-content-muted">Notas:</span>{' '}
          <span className="text-content">{t.notes}</span>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════════════════════════ */

function fmtPeriod(p: string) {
  const [y, m] = p.split('-')
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return `${months[parseInt(m, 10) - 1]} ${y}`
}

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <h4 className="flex items-center gap-1.5 text-xs font-bold text-content-muted uppercase tracking-wider">
      {icon}{children}
    </h4>
  )
}

function DataField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-medium text-content-subtle uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm ${highlight ? 'font-bold text-primary' : 'font-medium text-content'}`}>{value}</p>
    </div>
  )
}

function CalcCard({ title, subtitle, applies, amount, fmt, children }: {
  title: string; subtitle: string; applies: boolean; amount: string
  fmt: (v: string | number) => string; children?: React.ReactNode
}) {
  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 space-y-2.5 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-xs font-bold text-content">{title}</p>
          <p className="text-[10px] text-content-subtle leading-tight mt-0.5">{subtitle}</p>
        </div>
        <span className={`shrink-0 inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
          applies ? 'bg-success-surface text-success-content-soft' : 'bg-surface-muted text-content-subtle'
        }`}>
          {applies ? 'Aplica' : 'No aplica'}
        </span>
      </div>
      {children && <div className="space-y-1.5">{children}</div>}
      <div className="pt-2 border-t border-divider">
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-content-subtle uppercase font-semibold">Subtotal</span>
          <span className="text-sm font-bold text-content">{fmt(amount)}</span>
        </div>
      </div>
    </div>
  )
}

function CalcVar({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-[11px] gap-2">
      <span className="text-content-muted">{label}</span>
      <span className="font-semibold text-content text-right">{value}</span>
    </div>
  )
}

function CalcFormula({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-mono text-content-muted bg-glass-3 border border-divider rounded-md px-2 py-1.5 text-right">
      {children}
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Formulario de edición (dentro del modal)
   ══════════════════════════════════════════════════════════════════════════════ */

function TerminationEditForm({ termination: t, onClose, fmt }: { termination: Termination; onClose: () => void; fmt: (v: string | number) => string }) {
  const { mutate: update, isPending } = useUpdateTermination(t.id)
  const [termDate, setTermDate] = useState(t.termination_date)
  const [reason, setReason] = useState(t.reason)
  const [notes, setNotes] = useState(t.notes || '')
  const [severanceOverride, setSeveranceOverride] = useState(t.severance_amount || '')
  const [vacationOverride, setVacationOverride] = useState(t.vacation_payout || '')
  const [noticeOverride, setNoticeOverride] = useState(t.notice_period_payout || '')

  const handleSave = () => {
    const sev = parseFloat(severanceOverride) || 0
    const vac = parseFloat(vacationOverride) || 0
    const notice = parseFloat(noticeOverride) || 0
    update({
      termination_date: termDate, reason, notes,
      severance_amount: String(sev),
      vacation_payout: String(vac),
      notice_period_payout: String(notice),
      total_settlement: String(sev + vac + notice),
      status: 'draft',
    }, {
      onSuccess: () => onClose(),
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="terminationstab-fecha-desvinculacion-2" className={labelCls}>Fecha Desvinculación</label>
          <DatePicker id="terminationstab-fecha-desvinculacion-2" value={termDate} onChange={setTermDate} />
        </div>
        <SelectField
          dense
          label="Causal"
          value={reason}
          onChange={e => setReason(e.target.value)}
        >
          {Object.entries(REASON_LABELS).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
          ))}
        </SelectField>
        <TextField
          dense
          label="Notas"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          inputProps={{ type: 'text' }}
        />
      </div>

      <div className="p-3 bg-warning-surface border border-warning-border rounded-lg">
        <p className="text-xs font-bold text-warning-content mb-2">Montos del finiquito (editables para mutuo acuerdo)</p>
        <div className="grid grid-cols-3 gap-3">
          <TextField
            dense
            label="Indemnización por Años"
            value={severanceOverride}
            onChange={e => setSeveranceOverride(e.target.value)}
            inputProps={{ type: 'number', min: '0', step: '1' }}
          />
          <TextField
            dense
            label="Vacaciones Proporcionales"
            value={vacationOverride}
            onChange={e => setVacationOverride(e.target.value)}
            inputProps={{ type: 'number', min: '0', step: '1' }}
          />
          <TextField
            dense
            label="Aviso Previo"
            value={noticeOverride}
            onChange={e => setNoticeOverride(e.target.value)}
            inputProps={{ type: 'number', min: '0', step: '1' }}
          />
        </div>
        <p className="text-[10px] text-warning-content-soft mt-2">
          Use Calcular para obtener los montos legales, o edite manualmente para montos acordados (mutuo acuerdo).
          El total se recalcula automáticamente.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-surface">Cancelar</button>
        <button onClick={handleSave} disabled={isPending}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40">
          Guardar cambios
        </button>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Formulario de creación
   ══════════════════════════════════════════════════════════════════════════════ */

function TerminationForm({ onClose, toast }: { onClose: () => void; toast: any }) {
  const { data: empData } = useEmployees({ status: 'active', page_size: 9999 })
  const { mutate: create, isPending } = useCreateTermination()
  const [employee, setEmployee] = useState('')
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('necesidades_empresa')
  const [notes, setNotes] = useState('')

  return (
    <div className="p-4 bg-danger-surface border border-danger-border rounded-xl space-y-3 mb-4">
      <p className="text-xs font-bold text-danger-content-soft uppercase tracking-wider">Nuevo Finiquito</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SelectField
          dense
          label="Empleado"
          value={employee}
          onChange={e => setEmployee(e.target.value)}
        >
          <option value="">Seleccionar...</option>
          {(empData?.employees || []).map((e: any) => (
          <option key={e.id} value={e.id}>{e.full_name || `${e.first_name} ${e.last_name}`}</option>
          ))}
        </SelectField>
        <div>
          <label htmlFor="terminationstab-fecha-desvinculacion" className={labelCls}>Fecha Desvinculación</label>
          <DatePicker id="terminationstab-fecha-desvinculacion" value={date} onChange={setDate} />
        </div>
        <SelectField
          dense
          label="Causal"
          value={reason}
          onChange={e => setReason(e.target.value)}
        >
          <option value="renuncia">Renuncia Voluntaria</option>
          <option value="mutuo_acuerdo">Mutuo Acuerdo</option>
          <option value="necesidades_empresa">Necesidades Empresa (Art. 161)</option>
          <option value="art_160">Causal Disciplinaria (Art. 160)</option>
          <option value="vencimiento_plazo">Vencimiento del Plazo</option>
          <option value="fin_obra">Conclusión de la Obra</option>
          <option value="caso_fortuito">Caso Fortuito</option>
        </SelectField>
        <TextField
          dense
          label="Notas"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Opcional"
          inputProps={{ type: 'text' }}
        />
      </div>
      <p className="text-[10px] text-danger-solid">
        Al crear, el finiquito queda en borrador. Use el botón Calcular para obtener los montos según la legislación chilena.
      </p>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-surface">Cancelar</button>
        <button onClick={() => {
          if (!employee || !date) return
          create({ employee, termination_date: date, reason, notes } as any, {
            onSuccess: () => { toast.success('Finiquito creado en borrador. Calcule los montos.'); onClose() },
            onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
          })
        }} disabled={isPending || !employee || !date}
          className="px-3 py-1.5 text-xs font-semibold bg-danger-fill text-white rounded-lg hover:bg-danger-fill-hover disabled:opacity-40">
          Crear finiquito
        </button>
      </div>
    </div>
  )
}
